import React, { useEffect, useMemo, useState } from 'react';
import {
    Drawer, Form, Input, InputNumber, Row, Col, Button, Select,
    DatePicker, Typography, Divider, Spin, Alert
} from 'antd';
import {
    UserOutlined, AppstoreOutlined, DollarOutlined,
    CalendarOutlined, CreditCardOutlined, FileTextOutlined,
    CloseOutlined, CheckCircleOutlined, WalletOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { userActions } from '../../actions/userActions';
import { feeActions } from '../../actions/feeActions';
import { axiosBase } from '../../services/axios';

const { TextArea } = Input;
const { Text } = Typography;

export interface FeeInput {
    _id?: string;
    categoryId: string;
    serviceId: string;
    studentId: string;
    totalFee: number;
    paidAmount: number;
    pendingAmount: number;
    paymentDate: string;
    paymentMode: string;
    remarks?: string;
}

interface FeeFormModalProps {
    open: boolean;
    isSingleStudent?: boolean;
    title: string;
    onCancel: () => void;
    onFinish: (values: FeeInput) => void;
    form: any;
    preSelectedData?: {
        studentId: string;
        categoryId: string;
        serviceId: string;
        enrollmentId?: string;
        studentName?: string;
        totalFee?: number;
        paidAmount?: number;
        pendingAmount?: number;
    } | null;
    isLoading?: boolean;   // ← add
}

const paymentModeOptions = [
    { label: 'Cash', value: 'CASH' },
    { label: 'UPI', value: 'UPI' },
    { label: 'Card', value: 'CARD' },
    { label: 'Net Banking', value: 'NET_BANKING' },
    { label: 'Cheque', value: 'CHEQUE' },
    { label: 'Bank Transfer', value: 'BANK_TRANSFER' },
];

const FeeFormModal: React.FC<FeeFormModalProps> = ({
    open,
    title,
    onCancel,
    onFinish,
    form,
    preSelectedData,
    isLoading,
    isSingleStudent=false
}) => {
    const dispatch = useDispatch();
    const categories = useSelector((state: any) => state.getCategoryParamsReducer?.categorydata);
    const services = useSelector((state: any) => state.getServiceParamsReducer?.servicedata);
    const students = useSelector((state: any) => state.userListReducer);
    const roleListData = useSelector((state: any) => state.roleDataReducer);
    const feeSummaryState = useSelector((state: any) => state.getStudentFeeSummaryReducer);

    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [loadingStudentData, setLoadingStudentData] = useState(false);
    const [studentEnrollmentData, setStudentEnrollmentData] = useState<any>(null);
    const [enrollmentError, setEnrollmentError] = useState<string | null>(null);
    const [allEnrollments, setAllEnrollments] = useState<any[]>([]);
    const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<string | null>(null);

    const isPreSelected = !!(preSelectedData?.enrollmentId);
    const feeSummary = feeSummaryState?.summary || null;

    useEffect(() => {
        if (roleListData && roleListData.roleData) {
            const studentRole = roleListData.roleData.find((r: any) => r.roleName === 'Student');
            if (studentRole) {
                dispatch(userActions.userListRequest({ role: studentRole._id }));
            }
        }
    }, [roleListData, dispatch]);

    const allServices = useMemo(() => {
        const payload = services?.services ?? services?.data ?? services ?? [];
        return Array.isArray(payload) ? payload : [];
    }, [services]);

    const allCategories = useMemo(() => {
        const payload = categories?.categories ?? categories?.data ?? categories ?? [];
        return Array.isArray(payload) ? payload : [];
    }, [categories]);

    const categoryOptions = useMemo(() => {
        return allCategories.map((s: any) => ({
            label: s?.categoryName,
            value: s?._id,
        }));
    }, [allCategories]);

    const serviceOptions = useMemo(() => {
        if (selectedCategoryId) {
            const filteredServices = allServices.filter((s: any) => {
                const serviceCategoryId = s?.category?._id || s?.categoryId || s?.category || s?.categoryId?._id;
                return String(serviceCategoryId) === String(selectedCategoryId);
            });
            return filteredServices.map((c: any) => ({
                label: c?.serviceName,
                value: c?._id,
                price: c?.price,
            }));
        }
        return [];
    }, [allServices, selectedCategoryId]);

    const studentOptions = useMemo(() => {
        const users = students?.userList?.users ?? [];
        return (Array.isArray(users) ? users : []).map((s: any) => {
            const name = `${s?.firstName || ''} ${s?.lastName || ''}`.trim();
            const studentId = s?.enrolledNumber || s?.studentId;
            const displayLabel = studentId ? `${name} (${studentId})` : name;
            return {
                label: displayLabel,
                value: String(s?._id),
                key: String(s?._id),
                enrolledNumber: studentId,
            };
        });
    }, [students]);

    const handleStudentChange = async (studentId: string, preSelectEnrollmentId?: string) => {
        if (!studentId) {
            setStudentEnrollmentData(null);
            setEnrollmentError(null);
            setAllEnrollments([]);
            setSelectedEnrollmentId(null);
            form.setFieldsValue({
                categoryId: undefined, serviceId: undefined,
                totalFee: undefined, paidAmount: undefined, pendingAmount: undefined,
            });
            return;
        }

        setLoadingStudentData(true);
        setEnrollmentError(null);
        setAllEnrollments([]);
        setSelectedEnrollmentId(null);
        setStudentEnrollmentData(null);
        try {
            const enrollmentResponse = await axiosBase.get(`users/student/${studentId}/enrollment`);

            if (enrollmentResponse.data?.success && enrollmentResponse.data?.resultObject) {
                const resultObject = enrollmentResponse.data.resultObject;
                const enrollments: any[] = Array.isArray(resultObject) ? resultObject : [resultObject];

                if (enrollments.length === 0) {
                    setEnrollmentError('No active enrollment found for this student. Please enroll the student first.');
                    form.setFieldsValue({ categoryId: undefined, serviceId: undefined, totalFee: undefined, paidAmount: undefined, pendingAmount: undefined });
                } else if (enrollments.length === 1) {
                    const enrollmentData = enrollments[0];
                    setAllEnrollments(enrollments);
                    applyEnrollment(enrollmentData, studentId);
                } else {
                    setAllEnrollments(enrollments);
                    if (preSelectEnrollmentId) {
                        const match = enrollments.find(
                            (e: any) => String(e.enrollmentId) === String(preSelectEnrollmentId)
                        );
                        if (match) {
                            setSelectedEnrollmentId(preSelectEnrollmentId);
                            applyEnrollment(match, studentId);
                        } else {
                            form.setFieldsValue({ categoryId: undefined, serviceId: undefined, totalFee: undefined, paidAmount: undefined, pendingAmount: undefined });
                        }
                    } else {
                        form.setFieldsValue({ categoryId: undefined, serviceId: undefined, totalFee: undefined, paidAmount: undefined, pendingAmount: undefined });
                    }
                }
                setEnrollmentError(null);
            } else {
                setStudentEnrollmentData(null);
                setEnrollmentError('No active enrollment found for this student. Please enroll the student first.');
                form.setFieldsValue({ categoryId: undefined, serviceId: undefined, totalFee: undefined, paidAmount: undefined, pendingAmount: undefined });
            }
        } catch (error: any) {
            setStudentEnrollmentData(null);
            const errorMessage = error?.response?.data?.message || error?.message || 'Error fetching student enrollment data';
            if (errorMessage.toLowerCase().includes('no active enrollment') || error?.response?.status === 404) {
                setEnrollmentError('No active enrollment found for this student. Please enroll the student first.');
            } else {
                setEnrollmentError(errorMessage);
            }
            form.setFieldsValue({ categoryId: undefined, serviceId: undefined, totalFee: undefined, paidAmount: undefined, pendingAmount: undefined });
        } finally {
            setLoadingStudentData(false);
        }
    };

    const applyEnrollment = (enrollmentData: any, studentId?: string) => {
        setStudentEnrollmentData(enrollmentData);
        setSelectedCategoryId(enrollmentData.categoryId);

        const serviceId = typeof enrollmentData.serviceId === 'object' 
            ? enrollmentData.serviceId?._id 
            : enrollmentData.serviceId;
        const enrollmentId = enrollmentData.enrollmentId || enrollmentData._id;

        const service = allServices.find((s: any) => s._id === String(serviceId));
        const servicePrice = enrollmentData.price || enrollmentData.servicePrice || service?.price || 0;

        form.setFieldsValue({
            categoryId: enrollmentData.categoryId,
            serviceId: serviceId,
            totalFee: servicePrice,
            pendingAmount: servicePrice,
        });

        const sid = studentId || form.getFieldValue('studentId');
        if (sid && serviceId) {
            dispatch(feeActions.getStudentFeeSummary({ studentId: sid, serviceId, enrollmentId }));
        }
    };

    const handleEnrollmentSelect = (enrollmentId: string) => {
        setSelectedEnrollmentId(enrollmentId);
        const chosen = allEnrollments.find((e: any) => String(e.enrollmentId || e._id) === String(enrollmentId));
        if (chosen) {
            applyEnrollment(chosen, form.getFieldValue('studentId'));
        }
    };

    useEffect(() => {
        if (open && preSelectedData?.studentId) {
            form.setFieldsValue({ studentId: preSelectedData.studentId });
            handleStudentChange(preSelectedData.studentId, preSelectedData.enrollmentId).then(() => {
                if (preSelectedData.pendingAmount !== undefined) {
                    form.setFieldsValue({ pendingAmount: preSelectedData.pendingAmount });
                }
            });

            if (preSelectedData.serviceId) {
                dispatch(feeActions.getStudentFeeSummary({
                    studentId: preSelectedData.studentId,
                    serviceId: preSelectedData.serviceId,
                    enrollmentId: preSelectedData.enrollmentId,
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, preSelectedData?.studentId]);

    useEffect(() => {
        if (!isPreSelected && feeSummaryState?.isSuccess && feeSummary && studentEnrollmentData) {
            const totalFee = feeSummary.totalFee ?? (form.getFieldValue('totalFee') || 0);
            const alreadyPaid = feeSummary.totalPaid || 0;
            const pendingAmount = feeSummary.pendingAmount !== undefined
                ? feeSummary.pendingAmount
                : Math.max(0, totalFee - alreadyPaid);
            form.setFieldsValue({ totalFee, pendingAmount });
        }
    }, [isPreSelected, feeSummaryState?.isSuccess, feeSummary, studentEnrollmentData, form]);

    const handlePaidAmountChange = (value: number | null) => {
        const maxPayable = remainingBalance;
        const currentPayment = Math.min(value || 0, maxPayable);
        const pendingAfterPayment = maxPayable - currentPayment;
        form.setFieldsValue({ paidAmount: currentPayment, pendingAmount: pendingAfterPayment });
    };

    const handleFinish = (values: any) => {
        const payload = {
            categoryId: values.categoryId,
            serviceId: values.serviceId,
            studentId: values.studentId,
            totalFee: values.totalFee,
            enrollmentId : selectedEnrollmentId || (studentEnrollmentData?.enrollmentId || studentEnrollmentData?._id),
            paidAmount: values.paidAmount,
            pendingAmount: values.pendingAmount,
            paymentDate: values.paymentDate?.format('YYYY-MM-DD'),
            paymentMode: values.paymentMode,
            remarks: values.remarks || '',
        };
        onFinish(payload as FeeInput);
    };


    

    const handleCancel = () => {
        setSelectedCategoryId(null);
        setStudentEnrollmentData(null);
        setEnrollmentError(null);
        setAllEnrollments([]);
        setSelectedEnrollmentId(null);
        form.resetFields();
        onCancel();
    };


    
    const alreadyPaid = isPreSelected
        ? (preSelectedData!.paidAmount ?? 0)
        : (feeSummary?.totalPaid || 0);
    const totalFee = isPreSelected
        ? (preSelectedData!.totalFee ?? form.getFieldValue('totalFee') ?? 0)
        : (form.getFieldValue('totalFee') || studentEnrollmentData?.price || 0);
    const remainingBalance = Math.max(0, totalFee - alreadyPaid);

    return (
        <Drawer className="fee-drawer" open={open} onClose={handleCancel}
            width={window.innerWidth > 1400 ? 530 : 480}
            closable={false} destroyOnClose title={null}
            footer={
                <div className="fee-drawer-footer">
                    <Button className="fee-btn-cancel" onClick={handleCancel} disabled={!!isLoading}>Cancel</Button>
                    <Button className="fee-btn-save" type="primary" onClick={() => form.submit()}
                        loading={!!isLoading} disabled={!!isLoading}>
                        {isLoading ? 'Saving...' : 'Save Payment'}
                    </Button>
                </div>
            }
        >
            {/* ── Spinner Overlay ── */}
            {isLoading && (
                <div className="svc-spinner-overlay">
                    <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 40, color: 'var(--primary-color)' }} spin />}
                    />
                    <span style={{ color: 'var(--primary-color)', fontWeight: 500, fontSize: 14 }}>
                        Saving payment...
                    </span>
                </div>
            )}

            {/* ── Sticky Header ── */}
            <div className="fee-drawer-header">
                <div className="fee-drawer-header-inner">
                    <div className="fee-drawer-title-group">
                        <div className="fee-drawer-icon-box">
                            <WalletOutlined />
                        </div>
                        <div className="fee-drawer-title-text">
                            <span className="fee-drawer-title">{title}</span>
                            <span className="fee-drawer-subtitle">Record a fee payment for a student enrollment</span>
                        </div>
                    </div>
                    <button className="fee-drawer-close-btn" onClick={handleCancel} aria-label="Close">
                        <CloseOutlined />
                    </button>
                </div>
                <Divider className="fee-drawer-header-divider" />
            </div>

            <Spin spinning={loadingStudentData} tip="Loading enrollment...">
                <Form form={form} layout="vertical" onFinish={handleFinish} className="fee-form">
                    <div className="fee-form-section">
                        <Form.Item name="studentId" label="Student"
                            rules={[{ required: true, message: 'Please select a student' }]} className="fee-form-item" >
                            <Select className="fee-select" placeholder="Search and select a student" showSearch
                                allowClear loading={loadingStudentData} listHeight={200}
                                optionFilterProp="label"
                                disabled={isSingleStudent}
                                filterOption={(input, option: any) => String(option?.label || '').toLowerCase().includes(input.toLowerCase()) }
                                options={studentOptions}
                                suffixIcon={<UserOutlined className="fee-select-suffix" />}
                                onChange={(value) => {
                                    if (value) {
                                        handleStudentChange(value);
                                    } else {
                                        setStudentEnrollmentData(null);
                                        setEnrollmentError(null);
                                        setAllEnrollments([]);
                                        setSelectedEnrollmentId(null);
                                    }
                                }}
                            />
                        </Form.Item>

                        {allEnrollments.length > 1 && !isPreSelected && (
                            <Form.Item label="Select Enrollment / Service" required className="fee-form-item" >
                                <Select className="fee-select" placeholder="Select a service to pay for"
                                    value={selectedEnrollmentId ?? undefined} onChange={handleEnrollmentSelect}
                                    options={allEnrollments.map((e: any) => ({ label: e.serviceName, value: String(e.enrollmentId || e._id),}))}
                                />
                            </Form.Item>
                        )}

                        {enrollmentError && (
                            <div className="fee-alert-wrap">
                                <Alert
                                    message="Enrollment Not Found"
                                    description={enrollmentError}
                                    type="warning"
                                    showIcon
                                    className="fee-alert"
                                />
                            </div>
                        )}

                        {/* Fee fully paid */}
                        {remainingBalance === 0 && studentEnrollmentData && (
                            <div className="fee-alert-wrap">
                                <Alert message="Fee Fully Paid"
                                    description="This student has already paid the complete fee amount." type="success"
                                    showIcon className="fee-alert"
                                />
                            </div>
                        )}
                    </div>

                    {studentEnrollmentData && (
                        <>
                            <Divider className="fee-section-divider" />
                            <div className="fee-form-section">
                                <div className="fee-section-header">
                                    <DollarOutlined className="fee-section-icon" />
                                    <span className="fee-section-title">Fee Summary</span>
                                </div>

                                <div className="fee-summary-card">
                                    <div className="fee-summary-stat">
                                        <span className="fee-summary-label">Total Fee</span>
                                        <span className="fee-summary-value fee-summary-value--blue">
                                            ₹{totalFee?.toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                    <div className="fee-summary-divider" />
                                    <div className="fee-summary-stat">
                                        <span className="fee-summary-label">Already Paid</span>
                                        <span className="fee-summary-value fee-summary-value--green">
                                            ₹{alreadyPaid?.toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                    <div className="fee-summary-divider" />
                                    <div className="fee-summary-stat">
                                        <span className="fee-summary-label">Remaining</span>
                                        <span className={`fee-summary-value${remainingBalance > 0 ? ' fee-summary-value--orange' : ' fee-summary-value--green'}`}>
                                            ₹{remainingBalance?.toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                </div>

                                {feeSummary?.payments?.length > 0 && (
                                    <div className="fee-summary-meta">
                                        <CheckCircleOutlined className="fee-summary-meta-icon" />
                                        <Text className="fee-summary-meta-text">
                                            {feeSummary.payments.length} payment(s) recorded.&nbsp;
                                            Last payment: {dayjs(feeSummary.lastPaymentDate).format('DD/MM/YYYY')}
                                        </Text>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* <Divider className="fee-section-divider" /> */}

                    {/* ── Section: Classification ── */}
                    <div className="fee-form-section">
                       
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="categoryId"
                                    label="Category"
                                    rules={[{ required: true, message: 'Please select a category' }]}
                                    className="fee-form-item"
                                >
                                    <Select
                                        className="fee-select"
                                        placeholder="Select category"
                                        showSearch
                                        listHeight={200}
                                        disabled={!!studentEnrollmentData}
                                        filterOption={(input, option: any) =>
                                            option?.label?.toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={categoryOptions}
                                        onChange={(value) => {
                                            setSelectedCategoryId(value);
                                            form.setFieldsValue({ serviceId: undefined });
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="serviceId"
                                    label="Service"
                                    rules={[{ required: true, message: 'Please select a service' }]}
                                    className="fee-form-item"
                                >
                                    <Select
                                        className="fee-select"
                                        placeholder="Select service"
                                        showSearch
                                        listHeight={200}
                                        disabled={!!studentEnrollmentData}
                                        filterOption={(input, option: any) =>
                                            option?.label?.toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={serviceOptions}
                                        onChange={(value) => {
                                            if (value && !studentEnrollmentData) {
                                                const selectedService = serviceOptions.find((s: any) => s.value === value);
                                                if (selectedService?.price) {
                                                    const price = selectedService.price;
                                                    form.setFieldsValue({ totalFee: price, pendingAmount: price });
                                                }

                                                const sid = form.getFieldValue('studentId');
                                                if (sid) {
                                                    dispatch(feeActions.getStudentFeeSummary({ studentId: sid, serviceId: value }));
                                                }
                                            }
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                          <Divider className="fee-section-divider" />
                    </div>

                  

                    {/* ── Section: Payment Details ── */}
                    <div className="fee-form-section">
                       

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="totalFee"
                                    label="Total Fee"
                                    rules={[
                                        { required: true, message: 'Please enter total fee' },
                                        { type: 'number', min: 0, message: 'Amount must be positive' },
                                    ]}
                                    className="fee-form-item"
                                >
                                    <InputNumber
                                        className="fee-input-number fee-input-number--disabled"
                                        placeholder="Auto-filled"
                                        prefix="₹"
                                        min={0}
                                        precision={2}
                                        disabled={!!studentEnrollmentData}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="paidAmount"
                                    label="Payment"
                                    rules={[
                                        { required: true, message: 'Please enter payment amount' },
                                        { type: 'number', min: 1, message: 'Amount must be at least ₹1' },
                                        {
                                            validator: (_, value) => {
                                                if (value && value > remainingBalance && studentEnrollmentData) {
                                                    return Promise.reject(new Error(`Max payable: ₹${remainingBalance}`));
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                    className="fee-form-item"
                                >
                                    <InputNumber
                                        className="fee-input-number"
                                        placeholder="Amount to pay"
                                        prefix="₹"
                                        min={0}
                                        max={remainingBalance || undefined}
                                        precision={2}
                                        onChange={handlePaidAmountChange}
                                        disabled={remainingBalance === 0 && !!studentEnrollmentData}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="pendingAmount"
                                    label="Pending"
                                    className="fee-form-item"
                                >
                                    <InputNumber
                                        className="fee-input-number fee-input-number--disabled"
                                        placeholder="Auto-calculated"
                                        prefix="₹"
                                        precision={2}
                                        disabled
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="paymentDate"
                                    label="Payment Date"
                                    rules={[{ required: true, message: 'Please select payment date' }]}
                                    initialValue={dayjs()}
                                    className="fee-form-item"
                                >
                                    <DatePicker
                                        className="fee-datepicker"
                                        placeholder="Select date"
                                        format="DD-MM-YYYY"
                                        suffixIcon={<CalendarOutlined className="fee-datepicker-icon" />}
                                        disabledDate={(current) => current && current > dayjs().endOf('day')}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="paymentMode"
                                    label="Payment Mode"
                                    rules={[{ required: true, message: 'Please select payment mode' }]}
                                    className="fee-form-item"
                                >
                                    <Select
                                        className="fee-select"
                                        placeholder="Select payment mode"
                                        options={paymentModeOptions}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    {/* <Divider className="fee-section-divider" /> */}

                    {/* ── Section: Notes ── */}
                    <div className="fee-form-section fee-form-section--last">
                       

                        <Form.Item
                            name="remarks"
                            label={
                                <span className="fee-label">
                                    Remarks
                                </span>
                            }
                            className="fee-form-item"
                        >
                            <TextArea
                                className="fee-textarea"
                                rows={3}
                                autoSize={{ minRows: 3, maxRows: 5 }}
                                placeholder="Add any relevant notes about this payment…"
                                maxLength={500}
                                showCount
                            />
                        </Form.Item>
                    </div>

                </Form>
            </Spin>
        </Drawer>
    );
};

export default FeeFormModal;